import { apiClient } from './apiClient';
import type {
  PlanningWorkItem,
  PlanningWorkItemEvidence,
  PlanningWorkItemEvidenceFormPayload,
} from './strategicPlanningApi';

export type TasksCalendarUser = {
  userId: string;
  name: string;
  email?: string | null;
  isSelf: boolean;
  isSubordinate: boolean;
};

export type TasksCalendarEvent = {
  id: string;
  title: string;
  kind?: string;
  metaTitle?: string | null;
  marcoTitle?: string | null;
  description?: string;
  status: string;
  priority: string;
  plannedStartDate?: string;
  plannedEndDate?: string;
  ownerUserId?: string;
  ownerUserName?: string;
  executorUserId?: string;
  calendarIds?: string[];
  areas?: string[];
};

export type TasksCalendarPlanningObjectiveOption = {
  id: string;
  title: string;
  kind: string;
};

export type TasksCalendarParentWorkItemOption = {
  id: string;
  kind: string;
  title: string;
  objectiveId: string;
  objectiveTitle: string;
  cycleId: string;
  cycleName: string;
};

export type TasksCalendarObjectiveKind = 'Estrategico' | 'Tatico' | 'Operacional';

export type TasksCalendarCreateContext = {
  defaultObjectiveKind: TasksCalendarObjectiveKind;
  isOwnerTeamLeader: boolean;
  resolvedCycleId?: string;
  resolvedCycleName?: string;
  objectives: TasksCalendarPlanningObjectiveOption[];
  parentWorkItems: TasksCalendarParentWorkItemOption[];
};

export type TasksCalendarCreatePayload = {
  title: string;
  description?: string;
  priority?: string;
  plannedStartDate?: string;
  plannedEndDate?: string;
  ownerUserId: string;
  objectiveKind?: TasksCalendarObjectiveKind;
  objectiveId?: string;
  parentWorkItemId?: string;
  calendarIds?: string[];
  areas?: string[];
};

export type TasksCalendarUpdatePayload = {
  title: string;
  description?: string;
  priority?: string;
  plannedStartDate?: string;
  plannedEndDate?: string;
  ownerUserId?: string;
  calendarIds?: string[];
  areas?: string[];
};

export async function getTasksCalendarTask(taskId: string): Promise<PlanningWorkItem> {
  const response = await apiClient.get<PlanningWorkItem>(`/v1/operations/calendars/tasks-calendar/tasks/${taskId}`);
  return response.data;
}

export async function updateTasksCalendarTask(
  taskId: string,
  payload: TasksCalendarUpdatePayload,
): Promise<void> {
  await apiClient.put(`/v1/operations/calendars/tasks-calendar/tasks/${taskId}`, payload);
}

export async function cancelTasksCalendarTask(taskId: string, cancellationReason: string): Promise<void> {
  await apiClient.post(`/v1/operations/calendars/tasks-calendar/tasks/${taskId}/cancel`, {
    cancellationReason,
  });
}

export async function deleteTasksCalendarTask(taskId: string): Promise<void> {
  await apiClient.delete(`/v1/operations/calendars/tasks-calendar/tasks/${taskId}`);
}

export type TasksCalendarTaskListItem = {
  id: string;
  kind?: string;
  title: string;
  description?: string | null;
  descriptionPreview?: string | null;
  status: string;
  plannedStartDate?: string | null;
  plannedEndDate?: string | null;
  ownerUserId?: string | null;
  ownerUserName?: string | null;
  createdByUserId?: string | null;
  teamId?: string | null;
  objectiveId?: string | null;
  objectiveTitle?: string | null;
  parentWorkItemId?: string | null;
  parentWorkItemTitle?: string | null;
  parentWorkItemKind?: string | null;
  isUnassigned?: boolean;
  isOrphan?: boolean;
  canAssign?: boolean;
  canRelink?: boolean;
  canStart: boolean;
  canPause: boolean;
  canCancel: boolean;
  canDelete: boolean;
  canFinish?: boolean;
  canExtendDeadline?: boolean;
  canEvaluate?: boolean;
  isEvaluated?: boolean;
  areas?: string[];
};

export type TasksCalendarTaskListResult = {
  currentCycleId?: string | null;
  currentCycleName?: string | null;
  items: TasksCalendarTaskListItem[];
};

export type TasksCalendarTaskStatusFilter =
  | 'Planned'
  | 'InProgress'
  | 'Completed'
  | 'Cancelled'
  | 'Overdue';

export async function listTasksCalendarTasksForPanel(params: {
  from: string;
  to: string;
  userIds: string[];
  status?: TasksCalendarTaskStatusFilter;
}): Promise<TasksCalendarTaskListResult> {
  const response = await apiClient.get<TasksCalendarTaskListResult>(
    '/v1/operations/calendars/tasks-calendar/tasks/list',
    {
      params: {
        from: params.from,
        to: params.to,
        userIds: params.userIds,
        status: params.status,
      },
      paramsSerializer: {
        indexes: null,
      },
    },
  );
  return response.data;
}

export async function startTasksCalendarTask(taskId: string): Promise<PlanningWorkItem> {
  const response = await apiClient.post<PlanningWorkItem>(
    `/v1/operations/calendars/tasks-calendar/tasks/${taskId}/start`,
  );
  return response.data;
}

export async function finishTasksCalendarTask(
  taskId: string,
  fulfillmentResult: string,
): Promise<PlanningWorkItem> {
  const response = await apiClient.post<PlanningWorkItem>(
    `/v1/operations/calendars/tasks-calendar/tasks/${taskId}/finish`,
    { fulfillmentResult },
  );
  return response.data;
}

function toEvidenceFormData(payload: PlanningWorkItemEvidenceFormPayload): FormData {
  const formData = new FormData();
  formData.append('title', payload.title);
  formData.append('description', payload.description);
  if (payload.externalUrl) formData.append('externalUrl', payload.externalUrl);
  if (payload.removeImage) formData.append('removeImage', 'true');
  if (payload.image) formData.append('image', payload.image);
  return formData;
}

export async function addTasksCalendarTaskEvidence(
  taskId: string,
  payload: PlanningWorkItemEvidenceFormPayload,
): Promise<PlanningWorkItemEvidence> {
  const response = await apiClient.post<PlanningWorkItemEvidence>(
    `/v1/operations/calendars/tasks-calendar/tasks/${taskId}/evidences`,
    toEvidenceFormData(payload),
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
}

export async function updateTasksCalendarTaskEvidence(
  taskId: string,
  evidenceId: string,
  payload: PlanningWorkItemEvidenceFormPayload,
): Promise<PlanningWorkItemEvidence> {
  const response = await apiClient.put<PlanningWorkItemEvidence>(
    `/v1/operations/calendars/tasks-calendar/tasks/${taskId}/evidences/${evidenceId}`,
    toEvidenceFormData(payload),
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
}

export async function deleteTasksCalendarTaskEvidence(taskId: string, evidenceId: string): Promise<void> {
  await apiClient.delete(`/v1/operations/calendars/tasks-calendar/tasks/${taskId}/evidences/${evidenceId}`);
}

export async function fetchTasksCalendarEvidenceImageUrl(
  taskId: string,
  evidenceId: string,
): Promise<{ url: string }> {
  const response = await apiClient.get<{ url: string }>(
    `/v1/operations/calendars/tasks-calendar/tasks/${taskId}/evidences/${evidenceId}/image-url`,
  );
  return response.data;
}

export async function pauseTasksCalendarTask(taskId: string): Promise<void> {
  await apiClient.post(`/v1/operations/calendars/tasks-calendar/tasks/${taskId}/pause`);
}

export async function listTasksCalendarUsers(): Promise<TasksCalendarUser[]> {
  const response = await apiClient.get<TasksCalendarUser[]>('/v1/operations/calendars/tasks-calendar/users');
  return response.data;
}

export async function getTasksCalendarCreateContext(
  ownerUserId: string,
  taskStartDate?: string,
): Promise<TasksCalendarCreateContext> {
  const response = await apiClient.get<TasksCalendarCreateContext>(
    '/v1/operations/calendars/tasks-calendar/create-context',
    { params: { ownerUserId, taskStartDate } },
  );
  return response.data;
}

export async function listTasksCalendarEvents(params: {
  from: string;
  to: string;
  userIds: string[];
}): Promise<TasksCalendarEvent[]> {
  const response = await apiClient.get<TasksCalendarEvent[]>('/v1/operations/calendars/tasks-calendar/events', {
    params: {
      from: params.from,
      to: params.to,
      userIds: params.userIds,
    },
    paramsSerializer: {
      indexes: null,
    },
  });
  return response.data;
}

export async function createTasksCalendarTask(payload: TasksCalendarCreatePayload): Promise<void> {
  await apiClient.post('/v1/operations/calendars/tasks-calendar/tasks', payload);
}
